"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Chips } from "@woba/ds-web-next";
import { BrunaAvatar } from "@/core/components/bruna/bruna-avatar";
import { DS_COLOR, DS_TYPOGRAPHY } from "@/core/lib/ds-typography";
import { AGENT_THEMES } from "@/core/components/floating-chatbot/constants/agents";
import type {
  AgentShortcut,
  AgentType,
} from "@/core/components/floating-chatbot/types";
import { useHomeProfile } from "@/action-center/hooks/use-home-profile";
import {
  getShortcutPriority,
  sortByPriority,
} from "@/action-center/constants/home-profile";

const AGENT_ORDER: AgentType[] = ["operation", "estrategy", "experience"];

const chipValue = (agentType: AgentType, shortcut: AgentShortcut): string =>
  `${agentType}:${shortcut.action}`;

type ActionCenterWelcomeProps = {
  onShortcutClick: (agentType: AgentType, shortcut: AgentShortcut) => void;
};

/**
 * Welcome do Centro de ações com IA (PED-12262, PED-14256): avatar único da Bruna,
 * título "O que você quer resolver hoje?" e pills de sugestão. Reusa os shortcuts do
 * registry de modos (AGENT_THEMES) — mesmas actions do hub da janela flutuante.
 */
export function ActionCenterWelcome({
  onShortcutClick,
}: ActionCenterWelcomeProps) {
  const [translate] = useTranslation(["action-center", "core"]);
  const { profile, topIntents, interactions90d } = useHomeProfile();

  // Mesmas pills de sempre (AGENT_THEMES), apenas reordenadas pela relevância
  // do perfil comportamental da empresa. `explorer` com uso disperso ganha
  // uma priorização leve baseada em `top_intents`; `explorer` "a zero"
  // (cliente novo, sem interações) mantém a ordem estática do catálogo.
  const orderedShortcuts = useMemo(() => {
    const allShortcuts = AGENT_ORDER.flatMap((agentType) =>
      AGENT_THEMES[agentType].shortcuts.map((shortcut) => ({
        agentType,
        shortcut,
      })),
    );
    const priority = getShortcutPriority(profile, topIntents, interactions90d);
    return sortByPriority(
      allShortcuts,
      priority,
      (item) => item.shortcut.action,
    );
  }, [profile, topIntents, interactions90d]);

  // Sugestões no Chips do DS (variante suggestion). O Figma (718-16934) não traz o pill
  // "Novo" das sugestões, então `badgeKey` não é exibido aqui.
  const chipItems = useMemo(
    () =>
      orderedShortcuts.map(({ agentType, shortcut }) => ({
        value: chipValue(agentType, shortcut),
        label: translate(`core:${shortcut.textKey}`) as string,
      })),
    [orderedShortcuts, translate],
  );

  // Figma Chat-Bruna (6087:32895): hero no topo (avatar 96px — 80px no mobile —,
  // selo e textos) e as sugestões centralizadas logo acima do input, sem o título
  // "Você pode tentar:" (mantido só como nome acessível do grupo de chips).
  return (
    <div
      className="flex h-full flex-col items-center justify-between gap-8 overflow-y-auto"
      data-testid="action-center-welcome"
    >
      <div className="flex w-full flex-col items-center gap-4">
        <div className="flex flex-col items-center gap-2">
          {/* Uma só instância: 96px, reduzida para 80px (size-20) abaixo de md. */}
          <BrunaAvatar size={96} className="max-md:!size-20" />
          <div data-testid="action-center-bruna-badge">
            <Badge
              variant="neutral"
              label={translate("core:chatbot-bruna-name") as string}
            />
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-2 text-center">
          {/* Heading/Medium */}
          <h1
            style={{
              ...DS_TYPOGRAPHY.headingMedium,
              color: DS_COLOR.contentDefault,
            }}
          >
            {translate("action-center-title")}
          </h1>
          {/* Paragraph/Medium */}
          <p
            style={{
              ...DS_TYPOGRAPHY.paragraphMedium,
              color: DS_COLOR.contentDefault,
            }}
          >
            {translate("action-center-subtitle")}
          </p>
        </div>
      </div>

      <div className="flex w-full justify-center py-1">
        <Chips
          variant="suggestion"
          accessibilityLabel={translate("action-center-try") as string}
          items={chipItems}
          style={{ justifyContent: "center" }}
          onItemPress={(item) => {
            const selected = orderedShortcuts.find(
              ({ agentType, shortcut }) =>
                chipValue(agentType, shortcut) === item.value,
            );
            if (selected)
              onShortcutClick(selected.agentType, selected.shortcut);
          }}
        />
      </div>
    </div>
  );
}
